/**
 * Who may reach BAM.
 *
 * Single source of truth, because the answer was previously spelled out in four
 * places that had already drifted apart: AuthContext computed one predicate for
 * establishing a session and another for refreshing features, the sidebar gated on
 * `allowed_roles: ['admin']`, the floating bubble on `role === 'admin'`, and the
 * Vue boom-host menu on a third list that already included property-manager. The
 * `bam_access` flag existed the whole time and none of them read it, which is why
 * granting it to a property-manager changed nothing.
 *
 * Three copies exist by necessity and are kept identical by test, not by discipline:
 * this file, app/javascript/src/utils/bam-access.js (byte-identical — a spec in
 * __tests__/bamAccess.test.js compares them), and the role lists in boom-mcp's
 * src/services/bamRoleSchema.ts (pinned from both sides by rspec and jest). Edit here.
 *
 * Roles with no generated view schema are refused here as well as in boom-mcp.
 * There is nothing to scope their reads with, so showing them the entry point would
 * only produce a 403 one click later.
 */

// Roles that keep reading `public` — today's behaviour, unchanged.
// Mirrors UNRESTRICTED_ROLES in boom-mcp src/services/bamRoleSchema.ts.
export const BAM_UNRESTRICTED_ROLES = ['admin', 'superadmin']

// Roles with a generated view schema. Mirrors ROLE_SCHEMAS in the same file.
//
// An allowlist, not a denylist: an unclassified role would otherwise pass here and
// be refused at the handshake, so the menu would appear and the chat would break.
export const BAM_SUPPORTED_ROLES = [
  'property-manager',
  'field-pm',
  'coordinator',
  'communication-manager',
  'communication-agent',
  'sales-manager',
  'sales-agent',
  'accountant',
  'cleaning-manager',
  'cleaning-supervisor',
  'cleaner',
  'contractor',
  'hotel-reception',
]

// Product types where the assistant IS the product, so per-user entitlement is
// implied by the subscription. Previously read only by the session-establishing
// branch of AuthContext; folding it in here rather than dropping it, because for
// these tenants a missing flag is a provisioning detail, not a decision to withhold.
export const BAM_PRODUCT_TYPES = ['boom-host', 'boom-bam', 'boom-bam-only']

// Narrower: the product type whose whole tenant the backend admits regardless of role.
// Mirrors WHOLE_TENANT_PRODUCT_TYPES in boom-mcp and the third branch of validate_session.
export const WHOLE_TENANT_PRODUCT_TYPES = ['boom-host']

/**
 * @param {object|null|undefined} user - the /api/get-current-user payload
 * @returns {boolean}
 */
export function canAccessBam(user) {
  if (!user) return false
  if (user.super_admin === true) return true

  const role = (user.role || '').trim().toLowerCase()

  // Unconditional for admins: the menu never consulted the flag for them, and
  // User#grant_bam_access only sets it on create and on promotion.
  if (BAM_UNRESTRICTED_ROLES.includes(role)) return true

  // A boom-host tenant is entitled wholesale by the backend's own session gate, and
  // its users mostly carry no role at all — so the allowlist must not be what refuses
  // them. Kept to exactly that product type, matching the backend rather than the
  // wider BAM_PRODUCT_TYPES list, so the menu and the handshake agree.
  if (WHOLE_TENANT_PRODUCT_TYPES.includes(user.tenant?.product_type))
    return true

  // Otherwise the role check stands: a subscription entitles people, it does not
  // conjure a read scope for a role with no generated schema, and admitting one would
  // only fail at the handshake.
  if (!BAM_SUPPORTED_ROLES.includes(role)) return false

  return (
    user.bam_access === true ||
    BAM_PRODUCT_TYPES.includes(user.tenant?.product_type)
  )
}
